import React, { FC, MouseEvent, useCallback } from 'react';

import { isNumber, isUndefined, noop } from 'lodash';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ActionButtonVariants } from 'components/action-button';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyMyCode } from 'components/icons/loyalty-my-code';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { IconPhone } from 'components/icons/phone';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { OfferBadges } from 'components/offer-badges';
import { CustomDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import { useEnableOffersBadge } from 'hooks/use-enable-offers-badge';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useForceStoreSelectionFlagged } from 'hooks/use-force-store-selection';
import { TimeUnit } from 'pages/loyalty/loyalty-banner/types';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { OfferLocked } from 'pages/loyalty/offer-locked/offer-locked';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  IncentiveButtonVariation,
  defaultIncentiveButtonVariation,
} from 'state/launchdarkly/variations';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useFeaturesEarningCalculations } from 'state/loyalty/hooks/use-feature-earning-calculations';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { DefaultBackground, PointsNeededText } from '../incentive-card/incentive-card.styled';

import { POINTS_TRANSLATION_KEY, REDEEM_REWARD_TRANSLATION_KEY } from './constants';
import {
  BackgroundImage,
  BarLockIcon,
  ButtonContainer,
  ButtonsWrapper,
  DisclaimerText,
  ErrorMessage,
  ImageWrapper,
  LoadingWrapper,
  LockOverlay,
  OrderMoreButton,
  Padding,
  RemoveRewardsButton,
  RewardDescription,
  RewardDetailsContainer,
  RewardName,
  RewardPictureLock,
  StyledActionButton,
  StyledActionNotifyMeButton,
  StyledDisclaimerContainer,
  StyledInRestaurantIcon,
  StyledIncentiveEvaluationMessages,
  StyledLoyaltyButton,
  StyledLoyaltyButtonSubtitle,
  StyledLoyaltyButtonText,
  StyledLoyaltyButtonTitle,
  StyledMobileOrderIcon,
  StyledMyCodeIcon,
  StyledPhoneIcon,
  StyledPointsDisplay,
  StyledPointsDisplayTierLockedBar,
  StyledProgressMeter,
  StyledRewardImage,
  StyledSecondaryActionButton,
  StyledUpcomingOfferBadge,
  TierLockedBar,
  TierLockedText,
  TierRequirementTag,
  TierRequirementText,
  Wrapper,
  WrapperButton,
} from './incentive-details.styled';
import { IIncentiveDetailsViewProps } from './types';

export const IncentiveDetailsView: FC<IIncentiveDetailsViewProps> = ({
  id,
  name,
  image,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  tierLocked,
  requiredLoyaltyTier,
  tierPointsNeeded,
  closedOffer,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardClick,
  handleInRestaurantRedemptionClick,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsClick,
  incentiveEvaluationResults,
  offersInCooldown = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
  challengeIncentiveSelected = false,
  incentiveEndingIn,
  isUpcomingOffer,
  isWidget,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  useForceStoreSelectionFlagged();
  const { isAuthenticated, setOriginLocation } = useAuthContext();
  const { data, getContentForTier } = useFeaturesLoyaltyTiersContent();
  const { getTierEarningData } = useFeaturesEarningCalculations();
  const location = useLocation();
  const cdp = useCdpContext();
  const { language } = useLocale();
  const { noStoreSelected } = useStoreContext();

  const meterEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_METER_ON_REWARDS));
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);
  const enableABTestingOfferAndRewardDetailsButtons =
    useFlag<IncentiveButtonVariation>(
      LaunchDarklyFlag.ENABLE_AB_TESTING_OFFER_AND_REWARD_DETAILS_BUTTONS
    ) || defaultIncentiveButtonVariation;
  const enableABTestingOfferAndRewardDetailsButtonsDesign = useFlag(
    LaunchDarklyFlag.ENABLE_AB_TESTING_OFFER_AND_REWARD_DETAILS_BUTTONS_DESIGN
  );

  const { featureInvitationCode } = useFeatureInvitationCode();
  const { enableOfferBadges } = useEnableOffersBadge();

  const incentiveLocked = locked || tierLocked || closedOffer;

  const questBenefit =
    featureInvitationCode?.invitationCodeBenefit && featureInvitationCode?.invitationCodeBenefit[0];

  const incentiveDetails = {
    name: challengeIncentiveSelected ? questBenefit?.name?.locale : name,
    description: challengeIncentiveSelected ? questBenefit?.howToRedeem?.localeRaw : description,
    image: challengeIncentiveSelected ? questBenefit?.image?.locale : image,
    backgroundImage: <DefaultBackground />,
  };

  // Offers should show "Add to Order" while Rewards should show "Redeem Reward"
  let redeemText = formatMessage({
    id: isOffer ? 'addMobileOrder' : REDEEM_REWARD_TRANSLATION_KEY,
  });

  if (enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton) {
    redeemText =
      enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton[language] || redeemText;
  }

  let inRestaurantRedemptionText = formatMessage({ id: 'redeemInRestaurant' });

  if (enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton) {
    inRestaurantRedemptionText =
      enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton[language] ||
      inRestaurantRedemptionText;
  }

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const handleFavoriteClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handleToggleFavorite(id, name);
    },
    [handleToggleFavorite, id, name]
  );

  const handleOrderMoreClick = useCallback(() => {
    if (data?.loyaltyTiersOrderMoreButtonLink?.locale) {
      cdp.trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: 'Order More to become a Superking',
          Path: location.pathname,
        },
      });

      return navigate(data?.loyaltyTiersOrderMoreButtonLink?.locale);
    }
  }, [data?.loyaltyTiersOrderMoreButtonLink?.locale, navigate, cdp, location.pathname]);

  const handleRedirectToSignUp = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      setOriginLocation(location.pathname);
      navigate(routes.signUp);
    },
    [location.pathname, navigate, setOriginLocation]
  );

  const handleSubscribeOffer = useCallback(() => {
    toast.success(formatMessage({ id: 'offerUpcomingConfirmation' }));
  }, [formatMessage]);

  const hasBlockingErrors =
    !!errorMessage ||
    (offersInCooldown && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const disableMobileOrderButton =
    hasBlockingErrors ||
    hasServiceModeError(incentiveEvaluationResults) ||
    challengeIncentiveSelected;

  const disableInRestaurantOrderButton =
    hasBlockingErrors ||
    (DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults)) ||
    challengeIncentiveSelected;

  const getSafeTimeUnit = (unit: keyof typeof TimeUnit | undefined): TimeUnit | '' => {
    if (unit && Object.keys(TimeUnit).includes(unit)) {
      return TimeUnit[unit];
    }
    return '';
  };

  const entranceData = getTierEarningData(requiredLoyaltyTier);
  const timeUnitTarget = getSafeTimeUnit(
    entranceData?.pointsToEnterCalculationTimeUnit as keyof typeof TimeUnit
  );

  const isReversedButtons = Boolean(
    noStoreSelected &&
      (enableABTestingOfferAndRewardDetailsButtons.onlineOrderButton ||
        enableABTestingOfferAndRewardDetailsButtonsDesign)
  );

  const mobileOrderIconFill = disableMobileOrderButton
    ? Styles.color.grey.three
    : isReversedButtons
      ? Styles.color.red
      : Styles.color.white;

  const myCodeIconFill = disableInRestaurantOrderButton
    ? Styles.color.grey.three
    : !isReversedButtons || !canAddToMobileOrder
      ? Styles.color.red
      : undefined;

  return (
    <Wrapper>
      <ImageWrapper hasMargin={isWidget}>
        {enableFavoriteOffers && isAuthenticated() && isOffer && (
          <WrapperButton>
            <ButtonForIconFav
              data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
              onClick={handleFavoriteClick}
              aria-label={formatMessage({
                id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
              })}
              isTransparent={false}
            >
              {favoriteLoading ? (
                <LoadingWrapper>
                  <FavoriteLoading />
                </LoadingWrapper>
              ) : (
                <FavoriteIcon isFavorite={favorited} />
              )}
            </ButtonForIconFav>
          </WrapperButton>
        )}

        {challengeIncentiveSelected
          ? incentiveDetails.backgroundImage
          : backgroundImage && (
              <BackgroundImage
                alt={name}
                image={backgroundImage}
                isChallengeIncentive={challengeIncentiveSelected}
              />
            )}
        {incentiveDetails.image && (
          <StyledRewardImage
            alt={name}
            image={incentiveDetails.image}
            objectFitContain
            isChallengeIncentive={challengeIncentiveSelected}
          />
        )}
        {challengeIncentiveSelected && <OfferLocked />}
        {incentiveLocked && !isUpcomingOffer ? (
          <>
            <LockOverlay />
            <RewardPictureLock fill={primitive.$white} />
          </>
        ) : null}
      </ImageWrapper>

      <RewardDetailsContainer>
        {!isUpcomingOffer && enableOfferBadges && (
          <OfferBadges displayLoginRequiredBadge={false} endingIn={incentiveEndingIn} />
        )}
        {isUpcomingOffer && <StyledUpcomingOfferBadge />}
        {!isUndefined(pointCost) && !tierLocked && (
          <StyledPointsDisplay
            value={pointCost}
            suffix={formatMessage({ id: POINTS_TRANSLATION_KEY })}
          />
        )}
        {tierLocked && (
          <TierRequirementTag>
            <IconLoyaltyTiersLock />
            <TierRequirementText>
              {getContentForTier(requiredLoyaltyTier!)?.loyaltyTiersUIConfigName?.locale}
            </TierRequirementText>
          </TierRequirementTag>
        )}
        {incentiveDetails.name && (
          <RewardName data-testid="reward-name">{incentiveDetails.name}</RewardName>
        )}
        {incentiveDetails.description && (
          <RewardDescription content={incentiveDetails.description} />
        )}
        {locked && (
          <>
            {meterEnabled && isNumber(redemptionPercentage) && (
              <StyledProgressMeter percentage={redemptionPercentage} />
            )}
            {isNumber(remainingPointsNeededForRedemption) && (
              <PointsNeededText>
                {formatMessage(
                  { id: 'yourXPointsAway' },
                  {
                    points: <span>{remainingPointsNeededForRedemption}</span>,
                    pointsSlang: POINTS_TRANSLATION_KEY,
                  }
                )}
              </PointsNeededText>
            )}
          </>
        )}
        {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
        {/* padding element needed for safari / iOS native devices */}
        <Padding />
      </RewardDetailsContainer>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      {tierLocked && tierPointsNeeded! > 0 && (
        <TierLockedBar>
          <TierLockedText>
            {formatMessage(
              { id: `loyaltyTierEarnMorePoints${timeUnitTarget}` },
              {
                points: tierPointsNeeded,
                pointsLabel: formatMessage({ id: 'points' }),
                timeCount: entranceData?.pointsToEnterCalculationTimeInterval,
                tierName: getContentForTier(requiredLoyaltyTier!)?.loyaltyTiersUIConfigName?.locale,
              }
            )}
          </TierLockedText>
          <StyledPointsDisplayTierLockedBar
            value={formatMessage({ id: 'numberOfCrowns' }, { crowns: tierPointsNeeded })}
            icon={<BarLockIcon fill={primitive.bk.$fieryRed} />}
          />
          <StyledProgressMeter percentage={redemptionPercentage!} />
        </TierLockedBar>
      )}

      <ButtonsWrapper>
        {isUpcomingOffer ? (
          <ButtonContainer>
            {isAuthenticated() ? (
              <StyledActionNotifyMeButton
                data-testid="notify-when-available"
                onClick={handleSubscribeOffer}
                eventAttributes={{
                  Name: 'Notify Me When Available',
                  Path: location.pathname,
                  sanityId: id,
                }}
              >
                {formatMessage({ id: 'notifyWhenAvailableButton' })}
              </StyledActionNotifyMeButton>
            ) : (
              <StyledActionNotifyMeButton
                data-testid="sign-up-to-notify"
                onClick={handleRedirectToSignUp}
                eventAttributes={{
                  Name: 'Sign Up to Notify Me When Available',
                  Path: location.pathname,
                }}
              >
                {formatMessage({ id: 'signUpToNotifyWhenAvailable' })}
              </StyledActionNotifyMeButton>
            )}
          </ButtonContainer>
        ) : null}

        {!locked && !tierLocked && !isUpcomingOffer && (
          <ButtonContainer reversed={isReversedButtons}>
            {canAddToMobileOrder && !showRemoveRewardButton && (
              <>
                {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                  <StyledLoyaltyButton
                    onClick={handleRedeemRewardClick}
                    disabled={disableMobileOrderButton}
                    data-testid="redeem-to-mobile-order-btn"
                  >
                    <StyledMobileOrderIcon>
                      <IconPhone fill={primitive.$white} />
                    </StyledMobileOrderIcon>
                    <StyledLoyaltyButtonText>
                      <StyledLoyaltyButtonTitle>
                        {formatMessage({ id: 'loyaltyRedeemAddToMobileOrder' })}
                      </StyledLoyaltyButtonTitle>
                      <StyledLoyaltyButtonSubtitle>
                        {formatMessage({ id: 'loyaltyRedeemAddToMobileOrderDescription' })}
                      </StyledLoyaltyButtonSubtitle>
                    </StyledLoyaltyButtonText>
                  </StyledLoyaltyButton>
                ) : (
                  <StyledActionButton
                    disabled={disableMobileOrderButton}
                    onClick={handleRedeemRewardClick}
                    isLoading={checkingAvailability}
                    data-testid="redeem-to-mobile-order-btn"
                    startIcon={
                      enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton ? (
                        <StyledPhoneIcon fill={mobileOrderIconFill} />
                      ) : null
                    }
                    variant={
                      isReversedButtons
                        ? ActionButtonVariants.OUTLINE
                        : ActionButtonVariants.PRIMARY
                    }
                    legacyStyle={Boolean(
                      !enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton
                    )}
                  >
                    {redeemText}
                  </StyledActionButton>
                )}
              </>
            )}
            {canAddToInRestaurantOrder && !showRemoveRewardButton && (
              <>
                {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                  <StyledLoyaltyButton
                    onClick={handleInRestaurantRedemptionClick}
                    disabled={disableInRestaurantOrderButton}
                    data-testid="redeem-in-restaurant-btn"
                  >
                    <StyledInRestaurantIcon>
                      <IconLoyaltyMyCode />
                    </StyledInRestaurantIcon>
                    <StyledLoyaltyButtonText>
                      <StyledLoyaltyButtonTitle>
                        {formatMessage({ id: 'loyaltyRedeemScanInRestaurant' })}
                      </StyledLoyaltyButtonTitle>
                      <StyledLoyaltyButtonSubtitle>
                        {formatMessage({ id: 'loyaltyRedeemScanInRestaurantDescription' })}
                      </StyledLoyaltyButtonSubtitle>
                    </StyledLoyaltyButtonText>
                  </StyledLoyaltyButton>
                ) : (
                  <StyledSecondaryActionButton
                    disabled={disableInRestaurantOrderButton}
                    onClick={handleInRestaurantRedemptionClick}
                    isLoading={checkingAvailability}
                    data-testid="redeem-in-restaurant-btn"
                    startIcon={
                      enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton ? (
                        <StyledMyCodeIcon fill={myCodeIconFill} />
                      ) : null
                    }
                    variant={
                      canAddToMobileOrder
                        ? enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                          ? isReversedButtons
                            ? ActionButtonVariants.PRIMARY
                            : ActionButtonVariants.OUTLINE
                          : ActionButtonVariants.TEXT_ONLY
                        : ActionButtonVariants.OUTLINE
                    }
                    legacyStyle={Boolean(
                      !enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                    )}
                  >
                    {inRestaurantRedemptionText}
                  </StyledSecondaryActionButton>
                )}
              </>
            )}
          </ButtonContainer>
        )}

        {!isUpcomingOffer && showRemoveRewardButton && (
          <ButtonContainer>
            <RemoveRewardsButton
              onClick={handleRemoveRewardsClick}
              variant={ActionButtonVariants.OUTLINE}
            >
              {formatMessage({ id: 'removeRewardsFromCart' })}
            </RemoveRewardsButton>
          </ButtonContainer>
        )}

        {tierLocked && tierPointsNeeded! > 0 && (
          <ButtonContainer>
            <OrderMoreButton
              onClick={handleOrderMoreClick}
              variant={ActionButtonVariants.PRIMARY}
              skipLogEvent={true}
            >
              {data?.loyaltyTiersOrderMoreButtonText?.locale}
            </OrderMoreButton>
          </ButtonContainer>
        )}
      </ButtonsWrapper>

      {incentiveEvaluationResults && (
        <StyledIncentiveEvaluationMessages
          incentiveEvaluationResults={incentiveEvaluationResults}
          hideIcon
        />
      )}
      {isOffer && moreInfo && (
        <StyledDisclaimerContainer>
          <CustomDisclaimer content={moreInfo} />
        </StyledDisclaimerContainer>
      )}

      {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
    </Wrapper>
  );
};
